import React from "react";
import { Card, Col, Flex, Row, Typography } from "antd";
import {
  BankOutlined,
  FolderOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { actions as routerActions } from "redux-router5";
import { ConnectedAppLayout } from "../AppLayout";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import { routeNames } from "../common/routes";
import { palette } from "../theme";
import { useDashboardWorkOrders } from "./hooks/useDashboardWorkOrders";
import { useDashboardProjects } from "./hooks/useDashboardProjects";
import { useDashboardUsers } from "./hooks/useDashboardUsers";
import {
  countByStatus,
  countByServiceId,
  findOverdueTickets,
  findDueSoonTickets,
  findProjectsEndingSoon,
} from "./dashboard.utils";
import {
  OPEN_WO_STATUSES,
  DUE_SOON_DAYS,
  PROJECTS_ENDING_SOON_DAYS,
} from "./dashboard.constants";
import { StatCard } from "./components/StatCard";
import { WorkOrdersByStatusChart } from "./components/WorkOrdersByStatusChart";
import { WorkOrdersByServiceChart } from "./components/WorkOrdersByServiceChart";
import { OverdueWorkOrdersTable } from "./components/OverdueWorkOrdersTable";
import { ProjectsEndingSoonTable } from "./components/ProjectsEndingSoonTable";
import { TechnicianWorkloadTable } from "./components/TechnicianWorkloadTable";

const FLEET_STATS = (
  activeProjects: number,
  clients: number,
  technicians: number,
) => [
  {
    icon: <FolderOutlined />,
    value: activeProjects,
    label: "Active Projects",
    color: palette.dashBlue,
  },
  {
    icon: <BankOutlined />,
    value: clients,
    label: "Clients",
    color: palette.dashGreen,
  },
  {
    icon: <TeamOutlined />,
    value: technicians,
    label: "Active Technicians",
    color: palette.dashPurple,
  },
];

const DashboardPage: React.FC = () => {
  const dispatch = useDispatch();
  const handleNavigate = (route: string, id: string) => {
    dispatch(routerActions.navigateTo(route, { id }));
  };

  const woState = useDashboardWorkOrders();
  const projectState = useDashboardProjects();
  const userState = useDashboardUsers();

  const statusCounts = countByStatus(woState.tickets);
  const serviceCounts = countByServiceId(woState.tickets);
  const total = woState.tickets.length;
  const openCount = OPEN_WO_STATUSES.reduce(
    (sum, status) => sum + (statusCounts[status] ?? 0),
    0,
  );
  const inProgressCount = statusCounts["IN_PROGRESS"] ?? 0;
  const inReviewCount = statusCounts["IN_REVIEW"] ?? 0;
  const overdueTickets = findOverdueTickets(woState.tickets);
  const dueSoonTickets = findDueSoonTickets(woState.tickets, DUE_SOON_DAYS);
  const projectsEndingSoon = findProjectsEndingSoon(
    projectState.projects,
    PROJECTS_ENDING_SOON_DAYS,
  );

  const serviceEntries = Object.entries(serviceCounts)
    .map(([serviceId, count]) => ({
      serviceId,
      serviceName: woState.serviceById[serviceId]?.name ?? serviceId,
      count,
    }))
    .sort((a, b) => b.count - a.count);

  const activeProjectsCount = projectState.projects.filter(
    (p) => p.project_status === "ACTIVE",
  ).length;
  const clientCount = Object.keys(projectState.clientById).length;
  const technicianCount = userState.technicians.length;

  const fleetStats = FLEET_STATS(activeProjectsCount, clientCount, technicianCount);

  return (
    <ConnectedAppLayout>
      <PageTitleContainer title="Dashboard">
        <Flex vertical gap={16}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={5}>
              <StatCard
                title="Open Work Orders"
                value={openCount}
                subtitle={`${total} total`}
                accentColor={palette.dashBlue}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <StatCard
                title="In Progress"
                value={inProgressCount}
                accentColor={palette.dashOrange}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <StatCard
                title="In Review"
                value={inReviewCount}
                accentColor={palette.dashPurple}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <StatCard
                title="Overdue"
                value={overdueTickets.length}
                subtitle="past deadline"
                accentColor={palette.dashRed}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <StatCard
                title="Due Soon"
                value={dueSoonTickets.length}
                subtitle={`within ${DUE_SOON_DAYS} days`}
                accentColor={palette.accent}
                loading={woState.loading}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <WorkOrdersByStatusChart
                countByStatus={statusCounts}
                total={total}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} lg={8}>
              <WorkOrdersByServiceChart
                entries={serviceEntries}
                total={total}
                loading={woState.loading}
              />
            </Col>
            <Col xs={24} lg={8}>
              <Card
                title="Fleet Overview"
                loading={userState.loading || projectState.loading}
                style={{ height: "100%" }}
              >
                <Flex vertical gap={12}>
                  {fleetStats.map(({ icon, value, label, color }) => (
                    <Flex
                      key={label}
                      align="center"
                      gap={12}
                      style={{
                        background: `${color}14`,
                        border: `1px solid ${color}33`,
                        borderRadius: "var(--ant-border-radius-lg)",
                        padding: "var(--ant-padding-xs) var(--ant-padding-md)",
                      }}
                    >
                      <Flex
                        align="center"
                        justify="center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "var(--ant-border-radius-lg)",
                          background: `${color}22`,
                          color,
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      >
                        {icon}
                      </Flex>
                      <Flex vertical gap={0}>
                        <Typography.Text
                          style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}
                        >
                          {value}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {label}
                        </Typography.Text>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              <OverdueWorkOrdersTable
                tickets={overdueTickets}
                assigneeById={woState.assigneeById}
                serviceById={woState.serviceById}
                loading={woState.loading}
                onNavigate={handleNavigate}
              />
            </Col>
            <Col xs={24} lg={8}>
              <ProjectsEndingSoonTable
                projects={projectsEndingSoon}
                clientById={projectState.clientById}
                loading={projectState.loading}
                onNavigate={handleNavigate}
              />
            </Col>
          </Row>

          <TechnicianWorkloadTable
            tickets={woState.tickets}
            technicians={userState.technicians}
            loading={woState.loading || userState.loading}
          />
        </Flex>
      </PageTitleContainer>
    </ConnectedAppLayout>
  );
};

export default DashboardPage;
